import { useState, useEffect, useCallback } from 'react';
import {
  fetchWellConstruction,
  addWellElevationHistory,
  updateWellConstruction,
  type WellConstructionResponse,
  type WellConstructionUpdate,
} from '@/api/dataVault';

interface Props {
  wellId: string;
  onClose: () => void;
  /** Called after a re-survey is recorded, so the parent can refresh
   *  gauging charts/tables that depend on the (now epoch-aware) elevation. */
  onElevationHistoryChanged?: () => void;
}

function Field({ label, value, unit }: { label: string; value: string | number | null | undefined; unit?: string }) {
  const display = value !== null && value !== undefined && value !== '' ? `${value}${unit ? ` ${unit}` : ''}` : '--';
  return (
    <div>
      <div className="text-[10px] text-mono-500 tracking-wider mb-0.5">{label}</div>
      <div className="text-xs text-mono-100">{display}</div>
    </div>
  );
}

function EditField({
  label, value, onChange, unit, type = 'text', placeholder, helpText,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  unit?: string;
  type?: 'text' | 'number' | 'date';
  placeholder?: string;
  helpText?: string;
}) {
  return (
    <label className="block">
      <span className="text-[10px] text-mono-500 tracking-wider">{label}{unit ? ` (${unit})` : ''}</span>
      <input
        type={type === 'number' ? 'text' : type}
        inputMode={type === 'number' ? 'decimal' : undefined}
        placeholder={placeholder}
        className="mt-0.5 w-full bg-mono-950 border border-mono-700 rounded px-2 py-1 text-xs text-mono-100 focus:border-cyan-600 focus:outline-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {helpText && (
        <span className="mt-1 block text-[10px] leading-snug text-mono-500">{helpText}</span>
      )}
    </label>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

type EditState = Record<string, string>;

function blankEdit(d: WellConstructionResponse | null): EditState {
  const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  return {
    well_type: s(d?.well_type),
    well_group: s(d?.well_group),
    casing_elevation_ft: s(d?.casing_elevation_ft),
    ground_elevation_ft: s(d?.ground_elevation_ft),
    total_depth_ft: s(d?.total_depth_ft),
    screen_top_ft: s(d?.screen_top_ft),
    screen_bottom_ft: s(d?.screen_bottom_ft),
    casing_diameter_in: s(d?.casing_diameter_in),
    casing_material: s(d?.casing_material),
    specific_gravity: s(d?.specific_gravity),
    sort_order: s(d?.sort_order),
    install_date: s(d?.install_date),
    abandonment_date: s(d?.abandonment_date),
  };
}

export function WellConstructionPanel({ wellId, onClose, onElevationHistoryChanged }: Props) {
  const [data, setData] = useState<WellConstructionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    fetchWellConstruction(wellId)
      .then(setData)
      .catch((e) => {
        if (e?.response?.status === 404) {
          setNotFound(true);
          setData(null);
        } else {
          setError(e?.response?.data?.detail || 'Failed to load well construction.');
        }
      })
      .finally(() => setLoading(false));
  }, [wellId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ── Edit construction ───────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditState>({});
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const startEdit = () => { setEditForm(blankEdit(data)); setEditError(null); setEditing(true); };

  const saveEdit = async () => {
    const num = (v: string): number | null => {
      const t = v.trim();
      if (t === '') return null;
      const n = parseFloat(t);
      return isFinite(n) ? n : null;
    };
    const str = (v: string): string | null => (v.trim() === '' ? null : v.trim());
    const body: WellConstructionUpdate = {
      well_type: str(editForm.well_type),
      well_group: str(editForm.well_group),
      casing_elevation_ft: num(editForm.casing_elevation_ft),
      ground_elevation_ft: num(editForm.ground_elevation_ft),
      total_depth_ft: num(editForm.total_depth_ft),
      screen_top_ft: num(editForm.screen_top_ft),
      screen_bottom_ft: num(editForm.screen_bottom_ft),
      casing_diameter_in: num(editForm.casing_diameter_in),
      casing_material: str(editForm.casing_material),
      specific_gravity: num(editForm.specific_gravity),
      sort_order: num(editForm.sort_order),
      install_date: str(editForm.install_date),
      abandonment_date: str(editForm.abandonment_date),
    };
    setEditBusy(true);
    setEditError(null);
    try {
      const updated = await updateWellConstruction(wellId, body);
      setData(updated);
      setEditing(false);
      onElevationHistoryChanged?.();
    } catch (e: any) {
      setEditError(e?.response?.data?.detail || 'Failed to save well construction.');
    } finally {
      setEditBusy(false);
    }
  };

  // ── Re-survey form ──────────────────────────────────────────────────
  const [showResurvey, setShowResurvey] = useState(false);
  const [rsForm, setRsForm] = useState({ elevation_ft: '', effective_date: today(), surveyed_by: '', notes: '' });
  const [rsBusy, setRsBusy] = useState(false);
  const [rsError, setRsError] = useState<string | null>(null);

  const submitResurvey = async () => {
    const elev = parseFloat(rsForm.elevation_ft);
    if (!isFinite(elev)) { setRsError('Enter a valid TOC elevation (ft MSL).'); return; }
    if (!rsForm.effective_date) { setRsError('Pick an effective date.'); return; }
    setRsBusy(true);
    setRsError(null);
    try {
      await addWellElevationHistory(wellId, {
        elevation_ft: elev,
        effective_date: rsForm.effective_date,
        source: 'survey',
        surveyed_by: rsForm.surveyed_by.trim() || null,
        notes: rsForm.notes.trim() || null,
      });
      setShowResurvey(false);
      setRsForm({ elevation_ft: '', effective_date: today(), surveyed_by: '', notes: '' });
      reload();
      onElevationHistoryChanged?.();
    } catch (e: any) {
      setRsError(e?.response?.data?.detail || 'Failed to record re-survey.');
    } finally {
      setRsBusy(false);
    }
  };

  const history = data?.elevation_history ?? [];

  return (
    <div className="border border-mono-700 rounded-lg bg-mono-900/80 p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-xs font-medium text-mono-100 tracking-widest">WELL CONSTRUCTION</h3>
          {data && (
            <span className="text-xs text-mono-400">{data.well_name}{data.site_name ? ` @ ${data.site_name}` : ''}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {data && !loading && !editing && (
            <button
              onClick={startEdit}
              className="px-2 py-1 text-[10px] tracking-wider rounded border border-mono-700 text-mono-300 hover:text-mono-100 hover:border-mono-500 transition-colors"
            >
              EDIT
            </button>
          )}
          <button
            onClick={onClose}
            className="px-2 py-1 text-xs tracking-wider text-mono-400 hover:text-mono-100 transition-colors"
          >
            CLOSE
          </button>
        </div>
      </div>

      {loading && (
        <div className="py-8 text-center text-xs text-mono-500 tracking-wider">LOADING...</div>
      )}

      {error && (
        <div className="py-4 text-center text-xs text-red-400">{error}</div>
      )}

      {notFound && !loading && (
        <div className="py-4 text-center text-xs text-mono-500">
          No well-construction record found for this well. It isn't registered as a monitoring well at this site.
        </div>
      )}

      {data && !loading && !editing && (
        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-4 gap-4">
            <Field label="WELL TYPE" value={data.well_type?.toUpperCase()} />
            <Field label="STATUS" value={data.is_active ? 'ACTIVE' : 'INACTIVE'} />
            <Field label="WELL GROUP" value={data.well_group?.toUpperCase()} />
            <Field label="SORT ORDER" value={data.sort_order} />
          </div>

          {/* Elevations & Depths */}
          <div className="border-t border-mono-800 pt-3">
            <div className="text-[10px] text-mono-500 tracking-wider mb-2">ELEVATIONS & DEPTHS</div>
            <div className="grid grid-cols-4 gap-4">
              <Field label="CASING ELEV (TOC)" value={data.casing_elevation_ft?.toFixed(3)} unit="ft MSL" />
              <Field label="GROUND ELEV" value={data.ground_elevation_ft?.toFixed(2)} unit="ft MSL" />
              <Field label="TOTAL DEPTH" value={data.total_depth_ft?.toFixed(2)} unit="ft" />
              <Field label="SPECIFIC GRAVITY" value={data.specific_gravity?.toFixed(3)} />
            </div>
          </div>

          {/* Screen Interval */}
          <div className="border-t border-mono-800 pt-3">
            <div className="text-[10px] text-mono-500 tracking-wider mb-2">SCREEN INTERVAL</div>
            <div className="grid grid-cols-4 gap-4">
              <Field label="SCREEN TOP" value={data.screen_top_ft?.toFixed(2)} unit="ft" />
              <Field label="SCREEN BOTTOM" value={data.screen_bottom_ft?.toFixed(2)} unit="ft" />
              <Field
                label="SCREEN LENGTH"
                value={
                  data.screen_top_ft != null && data.screen_bottom_ft != null
                    ? (data.screen_bottom_ft - data.screen_top_ft).toFixed(2)
                    : null
                }
                unit="ft"
              />
              <Field label="CASING DIAMETER" value={data.casing_diameter_in?.toFixed(2)} unit="in" />
            </div>
          </div>

          {/* Casing Material & Dates */}
          <div className="border-t border-mono-800 pt-3">
            <div className="grid grid-cols-4 gap-4">
              <Field label="CASING MATERIAL" value={data.casing_material?.toUpperCase()} />
              <Field label="INSTALL DATE" value={data.install_date} />
              <Field label="ABANDONMENT DATE" value={data.abandonment_date} />
            </div>
          </div>
        </div>
      )}

      {/* Edit mode */}
      {data && !loading && editing && (
        <div className="space-y-3 rounded border border-mono-700 bg-mono-900/60 p-3">
          <div className="text-[10px] text-mono-500 tracking-wider">EDIT WELL CONSTRUCTION</div>
          <div className="grid grid-cols-4 gap-3">
            <EditField label="WELL TYPE" value={editForm.well_type} onChange={(v) => setEditForm((f) => ({ ...f, well_type: v }))} placeholder="monitoring" />
            <EditField label="WELL GROUP" value={editForm.well_group} onChange={(v) => setEditForm((f) => ({ ...f, well_group: v }))} />
            <EditField label="SORT ORDER" type="number" value={editForm.sort_order} onChange={(v) => setEditForm((f) => ({ ...f, sort_order: v }))} />
            <EditField
              label="SPECIFIC GRAVITY"
              type="number"
              value={editForm.specific_gravity}
              onChange={(v) => setEditForm((f) => ({ ...f, specific_gravity: v }))}
              placeholder="inherit"
              helpText="Overrides site default for this well only. Leave blank to inherit (default 0.800)."
            />
            <EditField label="CASING ELEV (TOC)" unit="ft MSL" type="number" value={editForm.casing_elevation_ft} onChange={(v) => setEditForm((f) => ({ ...f, casing_elevation_ft: v }))} />
            <EditField label="GROUND ELEV" unit="ft MSL" type="number" value={editForm.ground_elevation_ft} onChange={(v) => setEditForm((f) => ({ ...f, ground_elevation_ft: v }))} />
            <EditField label="TOTAL DEPTH" unit="ft" type="number" value={editForm.total_depth_ft} onChange={(v) => setEditForm((f) => ({ ...f, total_depth_ft: v }))} />
            <EditField label="CASING DIAMETER" unit="in" type="number" value={editForm.casing_diameter_in} onChange={(v) => setEditForm((f) => ({ ...f, casing_diameter_in: v }))} />
            <EditField label="SCREEN TOP" unit="ft" type="number" value={editForm.screen_top_ft} onChange={(v) => setEditForm((f) => ({ ...f, screen_top_ft: v }))} />
            <EditField label="SCREEN BOTTOM" unit="ft" type="number" value={editForm.screen_bottom_ft} onChange={(v) => setEditForm((f) => ({ ...f, screen_bottom_ft: v }))} />
            <EditField label="CASING MATERIAL" value={editForm.casing_material} onChange={(v) => setEditForm((f) => ({ ...f, casing_material: v }))} placeholder="PVC" />
            <div />
            <EditField label="INSTALL DATE" type="date" value={editForm.install_date} onChange={(v) => setEditForm((f) => ({ ...f, install_date: v }))} />
            <EditField label="ABANDONMENT DATE" type="date" value={editForm.abandonment_date} onChange={(v) => setEditForm((f) => ({ ...f, abandonment_date: v }))} />
          </div>
          <p className="text-[10px] text-mono-500">
            Note: changing the casing (TOC) elevation here updates the well's current value but doesn't create a survey epoch — use "+ SURVEY" to record a dated re-survey.
          </p>
          {editError && <p className="text-[10px] text-red-400">{editError}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setEditing(false); setEditError(null); }}
              className="px-3 py-1 text-[10px] tracking-wider rounded border border-mono-700 text-mono-300 hover:text-mono-100 transition-colors"
            >
              CANCEL
            </button>
            <button
              onClick={saveEdit}
              disabled={editBusy}
              className="px-3 py-1 text-[10px] tracking-wider rounded bg-cyan-600/20 border border-cyan-600/50 text-cyan-300 hover:bg-cyan-600/30 disabled:opacity-50 transition-colors"
            >
              {editBusy ? 'SAVING…' : 'SAVE'}
            </button>
          </div>
        </div>
      )}

      {/* Elevation History + Re-survey — available whenever the well resolved */}
      {data && !loading && !editing && (
        <div className="border-t border-mono-800 pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] text-mono-500 tracking-wider">
              ELEVATION HISTORY{history.length ? ` (${history.length} ${history.length === 1 ? 'ENTRY' : 'ENTRIES'})` : ''}
            </div>
            <button
              onClick={() => { setShowResurvey((v) => !v); setRsError(null); }}
              className="px-2 py-1 text-[10px] tracking-wider rounded border border-mono-700 text-mono-300 hover:text-mono-100 hover:border-mono-500 transition-colors"
            >
              {showResurvey ? 'CANCEL' : '+ SURVEY'}
            </button>
          </div>

          {showResurvey && (
            <div className="mb-3 rounded border border-mono-700 bg-mono-900/60 p-3 space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] text-mono-500 tracking-wider">NEW TOC ELEVATION (ft MSL)</span>
                  <input
                    type="text" inputMode="decimal"
                    className="mt-0.5 w-full bg-mono-950 border border-mono-700 rounded px-2 py-1 text-xs text-mono-100 focus:border-cyan-600 focus:outline-none"
                    value={rsForm.elevation_ft}
                    onChange={(e) => setRsForm((f) => ({ ...f, elevation_ft: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] text-mono-500 tracking-wider">EFFECTIVE DATE</span>
                  <input
                    type="date"
                    className="mt-0.5 w-full bg-mono-950 border border-mono-700 rounded px-2 py-1 text-xs text-mono-100 focus:border-cyan-600 focus:outline-none"
                    value={rsForm.effective_date}
                    onChange={(e) => setRsForm((f) => ({ ...f, effective_date: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] text-mono-500 tracking-wider">SURVEYED BY</span>
                  <input
                    type="text"
                    className="mt-0.5 w-full bg-mono-950 border border-mono-700 rounded px-2 py-1 text-xs text-mono-100 focus:border-cyan-600 focus:outline-none"
                    value={rsForm.surveyed_by}
                    onChange={(e) => setRsForm((f) => ({ ...f, surveyed_by: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] text-mono-500 tracking-wider">NOTES</span>
                  <input
                    type="text"
                    className="mt-0.5 w-full bg-mono-950 border border-mono-700 rounded px-2 py-1 text-xs text-mono-100 focus:border-cyan-600 focus:outline-none"
                    value={rsForm.notes}
                    onChange={(e) => setRsForm((f) => ({ ...f, notes: e.target.value }))}
                  />
                </label>
              </div>
              <p className="text-[10px] text-mono-500">
                Gaugings before this date keep their old elevation; this becomes the well's current TOC if it's the latest survey.
              </p>
              {rsError && <p className="text-[10px] text-red-400">{rsError}</p>}
              <div className="flex justify-end">
                <button
                  onClick={submitResurvey}
                  disabled={rsBusy}
                  className="px-3 py-1 text-[10px] tracking-wider rounded bg-cyan-600/20 border border-cyan-600/50 text-cyan-300 hover:bg-cyan-600/30 disabled:opacity-50 transition-colors"
                >
                  {rsBusy ? 'SAVING…' : 'RECORD RE-SURVEY'}
                </button>
              </div>
            </div>
          )}

          {history.length > 0 ? (
            <div className="border border-mono-800 rounded overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-mono-900/50 border-b border-mono-800">
                    <th className="px-3 py-1.5 text-left text-[10px] text-mono-400 tracking-wider">EFFECTIVE DATE</th>
                    <th className="px-3 py-1.5 text-left text-[10px] text-mono-400 tracking-wider">ELEVATION (ft)</th>
                    <th className="px-3 py-1.5 text-left text-[10px] text-mono-400 tracking-wider">SOURCE</th>
                    <th className="px-3 py-1.5 text-left text-[10px] text-mono-400 tracking-wider">SURVEYED BY</th>
                    <th className="px-3 py-1.5 text-left text-[10px] text-mono-400 tracking-wider">NOTES</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-mono-800/50">
                  {history.map((h, i) => (
                    <tr key={h.id} className="hover:bg-mono-800/30">
                      <td className="px-3 py-1.5 text-xs text-mono-200">
                        {h.effective_date}
                        {i === 0 && <span className="ml-2 text-[9px] text-cyan-400 tracking-wider">CURRENT</span>}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-mono-100">{h.elevation_ft.toFixed(3)}</td>
                      <td className="px-3 py-1.5 text-xs text-mono-300">{h.source}</td>
                      <td className="px-3 py-1.5 text-xs text-mono-400">{h.surveyed_by || '--'}</td>
                      <td className="px-3 py-1.5 text-xs text-mono-400 max-w-[200px] truncate">{h.notes || '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-[10px] text-mono-600">No survey history recorded. Add the as-built TOC with "+ SURVEY".</div>
          )}
        </div>
      )}
    </div>
  );
}
